import { SagaIterator } from '@redux-saga/core';
import { all, call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { statusMessageSaga } from 'pages/AppManagement/saga';
import { isEmpty, isEqual } from 'lodash';
import { history } from 'utils';
import { RequestSagaParams } from '../../types';
import { extractEmbedded, mapChannelStream } from '../AssetsManagement/mappings';
import * as Actions from './slice';
import * as Endpoints from './endpoints';
import { CurationTypes, QuestionAnswer } from './types';

export function* getClicks(): SagaIterator {
  try {
    const response = yield call(Endpoints.getClicks);
    const data =
      response.data._embedded && response.data._embedded.clicks && response.data._embedded.clicks.length > 0
        ? response.data._embedded.clicks
        : [];
    yield put(Actions.fetchClicksSuccess(data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchClicksFailure(error));
  }
}

export function* getInsights({ payload }: RequestSagaParams): SagaIterator {
  try {
    if (payload.data.session && payload.data.placement) {
      const response = yield call(Endpoints.getInsights, payload.data.session, payload.data.placement);
      yield put(Actions.fetchInsightsSuccess(response.data));
    } else {
      throw new Error('parameters missing in fetch insights.');
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchInsightsFailure(error));
  }
}

export function* getChannelList({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getChannelsByMarketer, payload);
    let streamsResponse, placementResponse;
    if (response?.data?._embedded?.channels && !isEmpty(response?.data?._embedded?.channels)) {
      streamsResponse = yield all(
        response?.data?._embedded?.channels.map((item: { id: string }) => {
          return call(Endpoints.getStreamsByChannel, item.id);
        })
      );
    }
    const streamList = streamsResponse.map((item: { data: any[] }) => item.data);
    const streams = extractEmbedded(streamList, 'streams');

    if (!isEmpty(streams)) {
      placementResponse = yield all(
        streams.map((stream: { id: string }) => {
          return call(Endpoints.getPlacementsByStream, stream.id);
        })
      );
    }
    yield put(
      Actions.getChannelsByMarketerIdSuccess(
        mapChannelStream({
          channelList: response.data,
          streamList: streamsResponse.map((item: { data: any[] }) => item.data),
          placementList: placementResponse.map((item: { data: any[] }) => item.data)
        })
      )
    );
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getChannelsByMarketerIdFailure(error));
  }
}

export function* getAllCurations({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getAllCurationsByMarketer);
    const curations = response.data?._embedded?.curations ? response.data?._embedded?.curations : [];
    yield put(Actions.getAllCurationsByMarketerIdSuccess(curations));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getAllCurationsByMarketerIdFailure(error));
  }
}

export function* updateCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.updateCuration, payload.data.id, payload.data.data);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.updateCurationSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated Curation.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateCurationFailure(error));
  }
}

export function* createCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.createCuration, payload.data);
    if (payload.callBack) {
      yield call(payload.callBack, response.data.id);
    }
    yield put(Actions.createCurationSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully created Curation.' });
    history.push(`/curations/${response.data.id}`);
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createCurationFailure(error));
  }
}

export function* deleteCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.deleteCuration, payload.id);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.deleteCurationSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully deleted Curation.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.deleteCurationFailure(error));
  }
}

export function* getCurationDetails({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getCurationDetails, payload.id);
    yield put(Actions.getCurationDetailsSuccess(response.data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getCurationDetailsFailure(error));
  }
}

export function* getAllSubCurationsByCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    let curationKey = '';
    let subType = '';
    const curationId = payload.data.curationId;
    if (payload.data.type.toString().toLowerCase() == CurationTypes.PERSONA.toString()) {
      curationKey = 'personae';
      subType = CurationTypes.PERSONA.toString().toUpperCase();
    }
    const response = yield call(Endpoints.getAllSubCurationsByCuration, curationId, curationKey);
    const subCurations =
      response.data?._embedded && response.data?._embedded[curationKey] ? response.data?._embedded[curationKey] : [];
    if (subCurations && subCurations.length > 0) {
      for (let i = 0; i < subCurations.length; i++) {
        subCurations[i]['type'] = subType;
        if (!subCurations[i]['background']) {
          subCurations[i]['background'] = '#833CE21A';
        }
        if (!subCurations[i]['icon']) {
          subCurations[i]['icon'] = 'profile-circle-linear';
        }
      }
    }
    if (payload.callBack) {
      yield call(payload.callBack, subCurations);
    }
    yield put(Actions.getAllSubCurationsByCurationSuccess(subCurations));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getAllSubCurationsByCurationFailure(error));
  }
}

export function* createSubCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    let curationKey = '';
    let subType = '';
    if (payload.type.toString().toLowerCase() == CurationTypes.PERSONA.toString()) {
      curationKey = 'personae';
      subType = CurationTypes.PERSONA.toString().toUpperCase();
    }
    const response = yield call(Endpoints.createSubCuration, payload.curationId, curationKey, payload.data);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.createSubCurationSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully created ' + subType + '.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createSubCurationFailure(error));
  }
}

export function* updateSubCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    let curationKey = '';
    let subType = '';
    if (payload.type.toString().toLowerCase() == CurationTypes.PERSONA.toString()) {
      curationKey = 'personae';
      subType = CurationTypes.PERSONA.toString().toUpperCase();
    }
    const response = yield call(Endpoints.updateSubCuration, payload.subCurationId, curationKey, payload.data);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.updateSubCurationSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated ' + subType + '.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateSubCurationFailure(error));
  }
}

export function* deleteSubCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    let curationKey = '';
    let subType = '';
    if (payload.type.toString().toLowerCase() == CurationTypes.PERSONA.toString()) {
      curationKey = 'personae';
      subType = CurationTypes.PERSONA.toString().toUpperCase();
    }
    const response = yield call(Endpoints.deleteSubCuration, payload.subCurationId, curationKey);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.deleteSubCurationSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully deleted ' + subType + '.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.deleteSubCurationFailure(error));
  }
}

export function* getSubCurationQnAs({ payload }: RequestSagaParams): SagaIterator {
  try {
    let subType = '';
    if (payload.data.type.toString().toLowerCase() == CurationTypes.PERSONA.toString()) {
      subType = CurationTypes.PERSONA.toString().toUpperCase();
    }
    const response = yield call(Endpoints.getSubCurationQnAs, payload.data.subCurationId, subType.toLowerCase());

    const qnas = response.data?._embedded && response.data?._embedded.qas ? response.data?._embedded.qas : [];

    yield put(Actions.getSubCurationQnAsSuccess(qnas));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getSubCurationQnAsFailure(error));
  }
}

export function* updateQnA({ payload }: RequestSagaParams): SagaIterator {
  try {
    if (payload.data.qnas) {
      const response = yield all(
        payload.data.qnas.map((item: QuestionAnswer) => {
          if (item.id) {
            return call(Endpoints.updateQnA, item.id, item);
          } else {
            return call(Endpoints.addQnA, payload.data.subCurationId, payload.data.type.toString().toLowerCase(), item);
          }
        })
      );

      yield put(Actions.updateQnASuccess(response));
      if (payload.callBack) {
        yield call(payload.callBack);
      }
      yield call(statusMessageSaga, { type: 'success', message: 'Update Success.' });
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateQnAFailure(error));
  }
}

export function* deleteQnA({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.deleteQnA, payload.qnAid);
    yield put(Actions.deleteQnASuccess(response));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Delete Q n A Success.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.deleteQnAFailure(error));
  }
}

export function* getCurations({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getCurations);
    const curations = response.data?._embedded?.curations ? response.data?._embedded?.curations : [];
    yield put(Actions.getCurationsSuccess(curations));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getCurationsFailure(error));
  }
}

export function* getChaptersByCuration({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getChaptersByCuration, { curationId: payload.curationId });
    const chapters = response.data?._embedded?.chapters || [];
    yield put(Actions.getChaptersByCurationSuccess(chapters));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getChaptersByCurationFailure(error));
  }
}

export function* getChapterDetails({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getChapterDetails, payload);
    yield put(Actions.getChapterDetailsSuccess(response.data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getChapterDetailsFailure(error));
  }
}

export function* createChapter({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { data } = payload;
    const response = yield call(Endpoints.createChapter, data);
    if (payload.callBack) {
      yield call(payload.callBack, response.data);
    }
    yield put(Actions.createChapterSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully created Chapter.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createChapterFailure(error));
  }
}

export function* updateChapter({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { data, originalChapter } = payload;
    const { id, qas, ...chapterFields } = data;

    const qasChanged = !isEqual(qas, originalChapter.qas);

    if (qasChanged) {
      yield call(Endpoints.updateChapterQAs, id, qas);
    }

    const response = yield call(Endpoints.updateChapter, id, chapterFields);

    yield put(Actions.updateChapterSuccess(response.data));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated Chapter.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateChapterFailure(error));
  }
}

export function* simulateQuestionForSocialFeed(action: any): SagaIterator {
  try {
    if (!action || !action.payload) {
      throw new Error('Invalid action structure');
    }

    const payload = action.payload;

    let socialFeedId, curationId, chapterId, questionId;

    if (typeof payload === 'object') {
      socialFeedId = payload.socialFeedId;
      curationId = payload.curationId;
      chapterId = payload.chapterId;
      questionId = payload.questionId;

      if (payload.data) {
        socialFeedId = socialFeedId || payload.data.socialFeedId;
        curationId = curationId || payload.data.curationId;
        chapterId = chapterId || payload.data.chapterId;
        questionId = questionId || payload.data.questionId;
      }
    }

    if (!socialFeedId) {
      throw new Error('Missing socialFeedId');
    }

    if (!curationId || !chapterId || !questionId) {
      throw new Error('Missing required data fields');
    }

    const data = { curationId, chapterId, questionId };
    const response = yield call(Endpoints.simulateQuestionForSuperfeed, socialFeedId, data);
    const responseWithQuestionId = {
      ...response.data,
      questionId
    };

    yield put(Actions.simulateQuestionForSocialFeedSuccess(responseWithQuestionId));
    if (payload.callBack) {
      yield call(payload.callBack, responseWithQuestionId);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.simulateQuestionForSocialFeedFailure(error));
  }
}

export function* deleteChapter(action: {
  type: string;
  payload: { chapterId: string; callBack?: () => void };
}): SagaIterator {
  const { payload } = action;
  try {
    const response = yield call(Endpoints.deleteChapter, payload.chapterId);
    yield put(Actions.deleteChapterSuccess(response));
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully deleted Chapter.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.deleteChapterFailure(error));
  }
}

export function* CurationManagementWatcherSaga(): SagaIterator {
  yield all([
    yield takeLatest(Actions.fetchClicks.type, getClicks),
    yield takeLatest(Actions.fetchInsights.type, getInsights),
    yield takeLatest(Actions.getChannelsByMarketerId.type, getChannelList),
    yield takeLatest(Actions.getAllCurationsByMarketerId.type, getAllCurations),
    yield takeLatest(Actions.updateCuration.type, updateCuration),
    yield takeLatest(Actions.createCuration.type, createCuration),
    yield takeLatest(Actions.deleteCuration.type, deleteCuration),
    yield takeLatest(Actions.getCurationDetails.type, getCurationDetails),
    yield takeEvery(Actions.getAllSubCurationsByCuration.type, getAllSubCurationsByCuration),
    yield takeLatest(Actions.createSubCuration.type, createSubCuration),
    yield takeLatest(Actions.updateSubCuration.type, updateSubCuration),
    yield takeLatest(Actions.deleteSubCuration.type, deleteSubCuration),
    yield takeLatest(Actions.getSubCurationQnAs.type, getSubCurationQnAs),
    yield takeLatest(Actions.updateQnA.type, updateQnA),
    yield takeLatest(Actions.deleteQnA.type, deleteQnA),
    yield takeLatest(Actions.getCurations.type, getCurations),
    yield takeLatest(Actions.getChaptersByCuration.type, getChaptersByCuration),
    yield takeLatest(Actions.getChapterDetails.type, getChapterDetails),
    yield takeLatest(Actions.createChapter.type, createChapter),
    yield takeLatest(Actions.updateChapter.type, updateChapter),
    yield takeEvery(Actions.simulateQuestionForSocialFeed.type, simulateQuestionForSocialFeed),
    yield takeLatest(Actions.deleteChapter.type, deleteChapter)
  ]);
}

export default CurationManagementWatcherSaga;
